// src/dev/DevMenu.tsx
//
// Dev-only menu. A truly invisible hotspot pinned to the bottom-right corner
// opens a small panel listing the toggles from devToggles.ts. Renders nothing
// in production builds (import.meta.env.DEV === false) — the whole tree is
// compiled out, so there is no hotspot to find or click in prod.
import { lazy, Suspense, useState } from 'react';
import { devToggles } from './devToggles';

// Behind the DEV flag at module level as well: a production build then has no
// import() left, so the preview and what it pulls in are not even emitted.
const LegalPreview = import.meta.env.DEV
    ? lazy(() => import('./LegalPreview'))
    : null;

// Size of the invisible corner hotspot, in px. Tweak to taste.
const HOTSPOT_SIZE = 24;

export const DevMenu = () => {
    // Hooks run unconditionally (rules-of-hooks); the dev gate is below them.
    const [open, setOpen] = useState(false);
    // Bump to re-render after a toggle mutates its external (localStorage) state.
    const [, force] = useState(0);
    const [legalPreview, setLegalPreview] = useState(false);
    if (!import.meta.env.DEV) return null;

    return (
        <>
            {/* Invisible clickable corner. No visual element by design. */}
            <button
                type="button"
                aria-label="Open dev menu"
                data-testid="dev-menu-hotspot"
                onClick={() => setOpen(o => !o)}
                style={{
                    position: 'fixed',
                    bottom: 0,
                    right: 0,
                    width: HOTSPOT_SIZE,
                    height: HOTSPOT_SIZE,
                    padding: 0,
                    margin: 0,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'default',
                    zIndex: 99999,
                }}
            />
            {open && (
                <div
                    role="dialog"
                    aria-label="Dev menu"
                    style={{
                        position: 'fixed',
                        bottom: HOTSPOT_SIZE + 4,
                        right: 8,
                        zIndex: 99999,
                        minWidth: 220,
                        padding: 10,
                        borderRadius: 6,
                        background: '#222',
                        color: '#fff',
                        font: '12px/1.4 monospace',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
                    }}
                >
                    <div style={{ opacity: 0.6, marginBottom: 6 }}>
                        DEV MENU
                    </div>
                    {devToggles.map(t => (
                        <label
                            key={t.id}
                            title={t.description}
                            style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                                padding: '4px 0',
                                cursor: 'pointer',
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={t.get()}
                                onChange={e => {
                                    t.set(e.target.checked);
                                    force(n => n + 1);
                                }}
                            />
                            {t.label}
                        </label>
                    ))}
                    {/* For authors of legal/: the dialog with the local
                        files. Makes no server calls. */}
                    <button
                        type="button"
                        onClick={() => {
                            setLegalPreview(true);
                            setOpen(false);
                        }}
                        style={{
                            marginTop: 6,
                            padding: '4px 8px',
                            width: '100%',
                            border: '1px solid #666',
                            borderRadius: 4,
                            background: 'transparent',
                            color: 'inherit',
                            font: 'inherit',
                            cursor: 'pointer',
                        }}
                    >
                        Preview legal dialog
                    </button>
                </div>
            )}
            {legalPreview && LegalPreview && (
                <Suspense fallback={null}>
                    <LegalPreview onClose={() => setLegalPreview(false)} />
                </Suspense>
            )}
        </>
    );
};
